import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Presentation, type LucideIcon } from 'lucide-react';
import { Button } from '@/components/ui';
import { Blocks, type Block } from './Blocks';
import { Rich } from './Rich';

export interface DocSection {
  title: string;
  blocks: Block[];
}

interface DocPageProps {
  badgeIcon: LucideIcon;
  badge: string;
  title: string;
  /** One line under the title: stage, base, the ask. */
  status: string;
  summary: string;
  top?: Block[];
  sections: DocSection[];
  contactEmail: string;
  /** Route of the companion pitch deck, if the page links to it. */
  deckPath?: string;
  footer: string;
  aside?: ReactNode;
}

/** A long-form investor document: the one-pager and the investor overview. */
export function DocPage({
  badgeIcon: BadgeIcon,
  badge,
  title,
  status,
  summary,
  top = [],
  sections,
  contactEmail,
  deckPath,
  footer,
  aside,
}: DocPageProps) {
  return (
    <main className="flex-1 overflow-y-auto">
      <div className="mx-auto max-w-4xl px-4 pb-12 pt-20 sm:px-6 md:py-12">
        <header className="mb-8 md:mb-10">
          <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-indigo-500/20 bg-indigo-500/10 px-4 py-2">
            <BadgeIcon className="h-4 w-4 text-indigo-400" />
            <span className="text-sm font-medium text-indigo-300">{badge}</span>
          </div>
          <h1 className="mb-3 text-section-mobile font-extrabold tracking-heading text-white md:text-[2.5rem] md:leading-[1.1]">
            {title}
          </h1>
          <p className="mb-5 text-xs font-semibold uppercase tracking-wider text-slate-500">{status}</p>
          <p className="max-w-3xl text-sm leading-relaxed text-slate-300 sm:text-base">
            <Rich text={summary} />
          </p>
        </header>

        {top.length > 0 && (
          <div className="mb-8 space-y-4 md:mb-10">
            <Blocks blocks={top} />
          </div>
        )}

        {sections.map((section, i) => (
          <section key={section.title} className="mb-8 md:mb-10">
            <div className="mb-3 flex items-baseline gap-2.5">
              <span className="text-[11px] font-bold tabular-nums text-indigo-400/70">
                {String(i + 1).padStart(2, '0')}
              </span>
              <h2 className="text-base font-bold tracking-subheading text-white sm:text-lg">{section.title}</h2>
              <span className="ml-1 h-px flex-1 bg-slate-700/50" />
            </div>
            <div className="space-y-3 sm:space-y-4">
              <Blocks blocks={section.blocks} />
            </div>
          </section>
        ))}

        {aside}

        <div className="flex flex-col gap-3 border-t border-slate-700/50 pt-6 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-col gap-3 sm:flex-row">
            <a href={`mailto:${contactEmail}`} className="w-full sm:w-auto">
              <Button variant="primary" className="w-full sm:w-auto" leftIcon={<Mail className="h-4 w-4" />}>
                {contactEmail}
              </Button>
            </a>
            {deckPath && (
              <Link to={deckPath} className="w-full sm:w-auto">
                <Button variant="outline" className="w-full sm:w-auto" leftIcon={<Presentation className="h-4 w-4" />}>
                  Pitch deck
                </Button>
              </Link>
            )}
          </div>
          <p className="text-xs text-slate-500">{footer}</p>
        </div>
      </div>
    </main>
  );
}
